import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { UserRoundIcon, BrainIcon, WrenchIcon, CircleCheckIcon, ArrowRightIcon, ArrowDownIcon, SettingsIcon, UsersIcon, FileTextIcon, BarChart3Icon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { proofIntro, stages, proofCards } from '../../data/richelieuProof';
import { RichelieuCard } from './proof/RichelieuCard';
import { CitizenMock, AtnaMock, FieldMock, TrackingMock } from './proof/JourneyMocks';
import { ResultsPanel } from './proof/ResultsPanel';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 14
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.3,
    ease: EASE
  }
};
const STAGE_ICONS = {
  citizen: UserRoundIcon,
  atna: BrainIcon,
  field: WrenchIcon,
  city: CircleCheckIcon
} as const;
const CARD_ICONS = {
  process: SettingsIcon,
  field: UsersIcon,
  trace: FileTextIcon,
  visibility: BarChart3Icon
} as const;
export function RichelieuProof() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: {
    fr: string;
    en: string;
  }) => isFR ? v.fr : v.en;
  const mocks = [<CitizenMock key="c" isFR={isFR} data-id="element-6446" data-mp="b22ixetyjxtr" />, <AtnaMock key="a" isFR={isFR} data-id="element-6447" data-mp="b344id8lo2pq" />, <FieldMock key="f" isFR={isFR} data-id="element-6448" data-mp="64108v0g20it" />, <TrackingMock key="t" isFR={isFR} data-id="element-6449" data-mp="652lttf365es" />];
  return <div className="relative max-w-[1440px] mx-auto" data-id="element-6450" data-mp="13r9r75j7s1s7">
      <div className="pointer-events-none absolute left-[-6%] top-[18%] w-[40%] h-[50%] rounded-full bg-[#EEF0FF] opacity-60 blur-[110px]" aria-hidden="true" data-id="element-6451" data-mp="13r017hy65umi" />

      {/* Intro */}
      <motion.div {...reveal} className="relative text-center max-w-[980px] mx-auto" data-id="element-6452" data-mp="u0olky54hwzs">
        <p className="font-sans text-[12.5px] font-semibold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-6453" data-mp="p3ew5ihqhlby">{pick(proofIntro.eyebrow)}</p>
        <h2 className="font-serif text-[2.4rem] sm:text-[3.2rem] xl:text-[3.7rem] text-[#121212] leading-[1.08] tracking-tight mt-4" data-id="element-6454" data-mp="1mjbzntlpcbrd">
          {pick(proofIntro.title)}
        </h2>
        <p className="font-serif text-[1.35rem] sm:text-[1.7rem] text-[#6B6F99] mt-2" data-id="element-6455" data-mp="3c0h9197husn7">{pick(proofIntro.subline)}</p>
        <p className="font-sans text-[16px] md:text-[17px] text-[#4A5160] leading-[1.6] mt-4 max-w-[800px] mx-auto" data-id="element-6456" data-mp="3gxg3jsv2l1lx">{pick(proofIntro.body)}</p>
      </motion.div>

      <motion.div {...reveal} transition={{
      ...reveal.transition,
      delay: 0.05
    }} className="relative mt-10 grid grid-cols-1 lg:grid-cols-[minmax(0,300px)_minmax(0,1fr)] xl:grid-cols-[minmax(0,322px)_minmax(0,1fr)] gap-5" data-id="element-6457" data-mp="377o4alc0zkb4">
        <div className="max-w-[440px] w-full mx-auto lg:max-w-none" data-id="element-6458" data-mp="2x85qkmmpkuri">
          <RichelieuCard isFR={isFR} data-id="element-6459" data-mp="2jc1xiwnkmsgm" />
        </div>

        <div className="flex flex-col gap-4 min-w-0" data-id="element-6460" data-mp="2o9btyw9do4dy">
          {/* Journey */}
          <div className="rounded-2xl border border-[#E8E8F0] bg-white/80 p-5 lg:p-6" data-id="element-6461" data-mp="2obetc6wf79vc">
            <ol className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] gap-x-3 gap-y-8 items-stretch" data-id="element-6462" data-mp="hh1ae4llzkl5">
              {stages.map((s, i) => {
              const Icon = STAGE_ICONS[s.key];
              return <Fragment key={s.key} data-id="element-6463" data-mp="221y3g3hgw288" data-mpi={i}>
                    <li className="flex flex-col min-w-0" data-id="element-6464" data-mp="21z2368i5d1gm">
                      <div className="flex items-start gap-3 xl:border-r-0" data-id="element-6465" data-mp="2gr48w96weook">
                        <span className="w-11 h-11 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-6466" data-mp="2bysyudig1mia">
                          <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.6} data-id="element-6467" data-mp="2pp676gz8z385" />
                        </span>
                        <div className="min-w-0" data-id="element-6468" data-mp="2ktquvtgw1fdu">
                          <p className="font-sans text-[14px] font-semibold uppercase tracking-[0.02em] text-[#4F46E5]" data-id="element-6469" data-mp="2zp6uee08m3hm">{pick(s.title)}</p>
                          <p className="font-sans text-[11.5px] text-[#5B6478] leading-snug mt-0.5" data-id="element-6470" data-mp="2uqlpw2dycxnd">{pick(s.line)}</p>
                          <p className="font-sans text-[11px] text-[#8A90A0] leading-snug" data-id="element-6471" data-mp="15cqpphn3axev">{pick(s.note)}</p>
                        </div>
                      </div>
                      <div className="mt-5 flex-1 flex items-start justify-center" data-id="element-6472" data-mp="3njgypolb82um">{mocks[i]}</div>
                      {i < stages.length - 1 && <ArrowDownIcon className="md:hidden w-5 h-5 text-[#4F46E5] mx-auto mt-5" strokeWidth={1.5} aria-hidden="true" data-id="element-6473" data-mp="1dbl0wfje2fk" />}
                    </li>
                    {i < stages.length - 1 && <li className="hidden xl:flex items-center pt-24" aria-hidden="true" data-id="element-6474" data-mp="34utrazfnv1dv">
                        <ArrowRightIcon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.3} data-id="element-6475" data-mp="39s29v14q17fn" />
                      </li>}
                  </Fragment>;
            })}
            </ol>
          </div>

          {/* Proof cards */}
          <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3" data-id="element-6476" data-mp="2m39b6wwpslhc">
            {proofCards.map(c => {
            const Icon = CARD_ICONS[c.key];
            return <li key={c.key} className="rounded-xl border border-[#E8E8F0] bg-white px-4 py-4 flex items-center gap-3.5" data-id="element-6477" data-mp="2q2i7o9j52xgl">
                  <span className="w-11 h-11 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-6478" data-mp="2dlqxjd6rrpum">
                    <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.6} data-id="element-6479" data-mp="29jch0zqkp26i" />
                  </span>
                  <span className="min-w-0" data-id="element-6480" data-mp="24l2ge9m2x9di">
                    <span className="block font-sans text-[14px] font-semibold text-[#121212]" data-id="element-6481" data-mp="1zm7pekdykeib">{pick(c.title)}</span>
                    <span className="block font-sans text-[12px] text-[#6B7180] leading-snug" data-id="element-6482" data-mp="1uv9a9o4phmgn">{pick(c.sub)}</span>
                  </span>
                </li>;
          })}
          </ul>

          <ResultsPanel isFR={isFR} data-id="element-6483" data-mp="1lvvuyetwi3o0" />
        </div>
      </motion.div>
    </div>;
}